import { useId, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  ModalDialog,
  Mtds,
  SaveError,
  type SinglePlan,
  useGetPlansQuery,
  useGetPlanByIdQuery,
  useMoveDestinationsQuery,
  useMovePlanMutation
} from 'prove-common'

export function MovePlanModal({
  plan,
  isOpen,
  setOpen
}: {
  plan: SinglePlan
  isOpen: boolean
  setOpen: (open: boolean) => void
}) {
  const id = useId()
  const [destination, setDestination] = useState<number>()
  const plans = useGetPlansQuery(isOpen)
  const detail = useGetPlanByIdQuery(isOpen ? plan.id : undefined)
  const allowed = useMoveDestinationsQuery('plan', isOpen ? plan.id : undefined)
  const mutation = useMovePlanMutation()
  const navigate = useNavigate()
  const current = detail.data
  const canSubmit =
    current?.etag &&
    current.parents[0] &&
    destination != null &&
    allowed.data?.includes(destination) &&
    !allowed.isFetching &&
    !allowed.isError &&
    !detail.isFetching &&
    !detail.isError &&
    !mutation.isPending
  return (
    <ModalDialog
      id={`move-plan-${id}`}
      open={isOpen}
      onClose={() => setOpen(false)}
      closedby='any'
    >
      <Mtds.Prose>
        <Mtds.Heading>Flytt plan</Mtds.Heading>
        <p>
          Planen og alt arbeidet under flyttes samlet. Ansvar og lokale statuser
          beholdes.
        </p>
      </Mtds.Prose>
      {allowed.isPending && <p role='status'>Laster mulige plasseringer …</p>}
      {allowed.isError && (
        <Mtds.Alert data-color='danger'>{allowed.error.message}</Mtds.Alert>
      )}
      {detail.isError && (
        <Mtds.Alert data-color='danger'>{detail.error.message}</Mtds.Alert>
      )}
      {plans.isError && (
        <Mtds.Alert data-color='danger'>{plans.error.message}</Mtds.Alert>
      )}
      {allowed.isSuccess && (
        <Mtds.Field>
          <Mtds.Field.Label htmlFor={`destination-${id}`}>
            Ny overordnet plan
          </Mtds.Field.Label>
          <Mtds.Select
            id={`destination-${id}`}
            value={destination ?? ''}
            disabled={mutation.isPending}
            onChange={(event) =>
              setDestination(
                event.target.value ? Number(event.target.value) : undefined
              )
            }
          >
            <option value=''>Velg plan</option>
            {plans.data?.plans
              .filter((candidate) => allowed.data.includes(candidate.id))
              .map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.description ?? `Plan ${candidate.id}`}
                </option>
              ))}
          </Mtds.Select>
          {allowed.data.length === 0 && (
            <p>
              Ingen tillatte plasseringer. Flytting krever tilgang til alt
              arbeidet i planen.
            </p>
          )}
        </Mtds.Field>
      )}
      {mutation.isError && (
        <SaveError error={mutation.error} action='flytte planen'>
          <p>
            Valgt plassering er beholdt. Last inn siste versjon før du prøver
            igjen hvis planen eller tilgangen er endret.
          </p>
          <Mtds.Button
            disabled={allowed.isFetching || detail.isFetching}
            onClick={() => {
              void Promise.all([allowed.refetch(), detail.refetch()])
            }}
          >
            Last inn siste versjon
          </Mtds.Button>
        </SaveError>
      )}
      <Mtds.Flex>
        <Mtds.Button
          onClick={() => setOpen(false)}
          disabled={mutation.isPending}
        >
          Avbryt
        </Mtds.Button>
        <Mtds.Button
          data-variant='primary'
          disabled={!canSubmit}
          onClick={async () => {
            if (!canSubmit || !current || destination == null) return
            try {
              await mutation.mutateAsync({
                planId: current.id,
                sourceParentId: current.parents[0].id,
                destinationPlanId: destination,
                etag: current.etag!
              })
              setOpen(false)
              navigate(`/plans/${current.id}`)
              Mtds.toast.success('Planen er flyttet')
            } catch {
              /* Mutation error is rendered in the dialog. */
            }
          }}
        >
          Bekreft flytting
        </Mtds.Button>
      </Mtds.Flex>
    </ModalDialog>
  )
}
